import { PIECE_SIZE } from '../../core/constants.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { getUIDefinition } from '../../shared/specials-public.js';

const MIX_OVERLAY_ID = 'mix-overlay';
const MIX_FLASH_CLASS = 'mix-flash';
const MIX_TOP_Z = 20;
const MIX_POP_DELAY_MS = 140;
const MIX_POP_DURATION_MS = 360;
const MIX_RESTORE_DELAY_MS = 30;
const MIX_FALLBACK_CLEANUP_DELAY_MS = 150;
const MIX_FLASH_ANIMATION_MS = 420;
const MIX_POST_POP_HOLD_MS = 100;

function ensureMixOverlay(board) {
  let overlay = board.querySelector(`#${MIX_OVERLAY_ID}`);
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = MIX_OVERLAY_ID;
    overlay.classList.add('mix-overlay');
    overlay.style.position = 'absolute';
    overlay.style.inset = '0';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '7';
    overlay.style.transformOrigin = 'top left';
    board.appendChild(overlay);
  }
  return overlay;
}

function getBoardScale(board) {
  if (!board) return 1;
  const style = window.getComputedStyle(board);
  const transform = style.transform;

  if (!transform || transform === 'none') {
    return 1;
  }

  const matrix3d = transform.match(/matrix3d\(([^)]+)\)/);
  if (matrix3d) {
    const values = matrix3d[1].split(',').map((value) => Number(value.trim())) || [];
    if (values.length >= 16) {
      return Math.hypot(values[0], values[1], values[2]) || 1;
    }
  }

  const matrix = transform.match(/matrix\(([^)]+)\)/);
  if (matrix) {
    const values = matrix[1].split(',').map((value) => Number(value.trim())) || [];
    if (values.length >= 6) {
      return Math.hypot(values[0], values[1]) || 1;
    }
  }

  return 1;
}

function createFlashElement() {
  const flash = document.createElement('div');
  flash.classList.add(MIX_FLASH_CLASS);
  flash.setAttribute('aria-hidden', 'true');
  return flash;
}

function createMixPopElement(mixPieceType) {
  const pop = document.createElement('div');
  pop.classList.add('mix-pop');
  const piece = document.createElement('div');
  piece.classList.add('mix-pop-content', 'piece');
  if (mixPieceType) {
    const def = getUIDefinition(mixPieceType);
    def?.cssClasses?.forEach((cls) => piece.classList.add(cls));
  }
  pop.appendChild(piece);
  return pop;
}

function positionOverlayElement(element, wrapper, board) {
  const targetBoard = board || document.getElementById('board');
  if (!targetBoard) return;
  const boardRect = targetBoard.getBoundingClientRect();
  const rect = wrapper.getBoundingClientRect();
  const scale = getBoardScale(targetBoard) || 1;
  element.style.position = 'absolute';
  element.style.left = `${(rect.left - boardRect.left) / scale}px`;
  element.style.top = `${(rect.top - boardRect.top) / scale}px`;
  element.style.width = `${rect.width / scale}px`;
  element.style.height = `${rect.height / scale}px`;
}

function resetMixStyles(element) {
  if (!element) return;
  element.style.zIndex = '';
  element.style.position = '';
}
/**
 * @fileoverview User Input Handlers Module - ユーザー入力処理専門モジュール
 * ユーザー入力に対するUI応答とゲーム操作の統合処理
 *
 * 責務:
 * - ドラッグ&ドロップのUI変形処理
 * - スワイプ操作の座標計算とターゲット検出
 * - スワップアニメーション（ピース交換の視覚効果）
 * - 無効操作時のフィードバック（シェイクアニメーション）
 * - タッチ/マウスイベントの統合処理
 */

// getCellElementOrNull inlined

// DOM要素の参照
const table = document.getElementById('table');

// CSS定数
const PIECE_SHADOW_SELECTOR = '.piece-shadow';
const TRANSFORM_ZERO = 'translate(0px, 0px)';
const TRANSITION_TRANSFORM = 'transform 0.3s';
const DRAG_DATA_ATTRS = ['data-x', 'data-y', 'data-start-x', 'data-start-y'];

/**
 * DOM要素取得ヘルパー（共通化）
 * @param {number} row - 行
 * @param {number} col - 列
 * @returns {HTMLElement|null} ピース要素
 */
function getPieceElement(row, col) {
  if (!table) return null;
  const tr = table.rows[row];
  if (!tr) return null;
  const td = tr.cells[col];
  return td ? td.querySelector(PIECE_SHADOW_SELECTOR) : null;
}

/**
 * ドラッグ変形のUI処理
 * @deprecated dom-primitives の setTransform + setDataAttributes を使用してください
 * @param {HTMLElement} target - 対象要素
 * @param {number} x - X座標
 * @param {number} y - Y座標
 */
export function updateDragTransformUI(target, x, y) {
  if (!target) return;
  // Always apply translation on the wrapper layer (.piece-shadow)
  const shadow =
    typeof target.closest === 'function' ? target.closest(PIECE_SHADOW_SELECTOR) : null;
  const wrapper = shadow || target;
  wrapper.style.transform = `translate(${x}px, ${y}px)`;
  wrapper.dataset.x = x;
  wrapper.dataset.y = y;
}

/**
 * スワイプ方向から新しい座標を計算
 * @param {number} row - 現在の行
 * @param {number} col - 現在の列
 * @param {number} diffX - X方向の移動量
 * @param {number} diffY - Y方向の移動量
 * @returns {Object|null} 新しい座標 {newRow, newCol} または null
 */
function calculateSwipeTarget(row, col, diffX, diffY) {
  const absX = Math.abs(diffX);
  const absY = Math.abs(diffY);
  const axis = absX >= absY ? 'x' : 'y';
  const stepX = axis === 'x' ? Math.sign(diffX) : 0;
  const stepY = axis === 'y' ? Math.sign(diffY) : 0;
  const newRow = row + stepY;
  const newCol = col + stepX;

  // 盤面範囲外へのスワイプは無効（9x9の範囲チェック）
  if (newRow < 0 || newRow >= 9 || newCol < 0 || newCol >= 9) {
    return null;
  }

  return { newRow, newCol };
}

/**
 * スワイプ対象取得のUI処理
 * @param {number} startX - 開始X座標
 * @param {number} startY - 開始Y座標
 * @param {number} diffX - X方向の移動量
 * @param {number} diffY - Y方向の移動量
 * @returns {Object|null} スワイプ結果
 */
export function getSwipeTargetUI(startX, startY, diffX, diffY) {
  const target = document.elementFromPoint(startX, startY);
  if (!target || !target.classList.contains('piece')) {
    return null;
  }

  // .pieceの親要素から<td>を探す（.piece-shadow等がある場合に対応）
  const td = target.closest('td');
  if (!td) return null;

  const col = td.cellIndex;
  const tr = td.parentElement;
  const row = tr?.rowIndex;

  // cellIndexとrowIndexが有効な数値であることを確認
  // （DOM構造が不正な場合やボード外の場合にundefinedになる）
  if (typeof col !== 'number' || typeof row !== 'number' || col < 0 || row < 0) {
    return null;
  }

  // 軸ロック + 1マス限定（スワイプは隣接マスのみ）
  const swipeTarget = calculateSwipeTarget(row, col, diffX, diffY);
  if (!swipeTarget) {
    return null;
  }

  return {
    row,
    col,
    newRow: swipeTarget.newRow,
    newCol: swipeTarget.newCol,
    target,
  };
}

/**
 * ドラッグ属性リセットのUI処理
 * @deprecated dom-primitives の setTransform + setDataAttributes を使用してください
 * @param {HTMLElement} target - 対象要素
 */
export function resetDragAttributesUI(target) {
  if (target) {
    target.style.transform = TRANSFORM_ZERO;
    DRAG_DATA_ATTRS.forEach((attr) => target.removeAttribute(attr));
  }
}

/**
 * シェイクしながら元位置に戻すアニメーション
 * @param {HTMLElement} target - 対象要素
 */
export function animateReturnWithShake(target) {
  if (!target) return;

  const duration = 400;
  target.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
  target.style.transform = 'translate(0px, 0px)';

  setTimeout(() => {
    if (target.style) {
      target.style.transition = '';
      target.style.transform = '';
      DRAG_DATA_ATTRS.forEach((attr) => target.removeAttribute(attr));
    }
  }, duration);
}

/**
 * 要素のトランジションと変形設定のUI処理
 * @deprecated dom-primitives の setTransition + setTransform を使用してください
 * @param {HTMLElement} element - 対象要素
 * @param {string} transition - トランジション設定
 * @param {string} transform - 変形設定
 * @param {number} [opacity] - 透明度（オプション）
 */
export function setTransitionAndTransformUI(element, transition, transform, opacity) {
  if (element) {
    element.style.transition = transition;
    element.style.transform = transform;
    if (opacity !== undefined) {
      element.style.opacity = opacity;
    }
  }
}

/**
 * ノードの位置交換のUI処理
 * @deprecated dom-primitives の swapNodes を使用してください
 * @param {HTMLElement} node1 - ノード1
 * @param {HTMLElement} node2 - ノード2
 */
export function swapNodesUI(node1, node2) {
  if (node1 && node2) {
    const parent1 = node1.parentNode;
    const parent2 = node2.parentNode;
    if (parent1 && parent2) {
      parent1.removeChild(node1);
      parent2.removeChild(node2);
      parent1.appendChild(node2);
      parent2.appendChild(node1);
    }
  }
}

/**
 * 合体アニメーションのUI処理（Mix専用）
 * piece1がpiece2に向かって移動し、合体位置でスムーズに1つに合成
 * @param {Object} piece1 - ピース1 ({ position: [row, col], specialType })
 * @param {Object} piece2 - ピース2 ({ position: [row, col], specialType })
 * @param {Array} targetPosition - 合体位置 [row, col]
 * @param {string|null} mixPieceType - Mix結果の特殊ピースタイプ
 * @param {Function} onComplete - 完了コールバック
 */
export function animateMergeUI(piece1, piece2, targetPosition, mixPieceType, onComplete) {
  const context = prepareMergeContext(piece1, piece2, targetPosition, mixPieceType);
  if (!context) {
    onComplete();
    return;
  }

  startPrimaryMergeAnimation(context);
  scheduleMergeFinale(context, onComplete);
}

function prepareMergeContext(piece1, piece2, targetPosition, mixPieceType) {
  const [row1, col1] = piece1.position;
  const [row2, col2] = piece2.position;
  const [targetRow, targetCol] = targetPosition;

  const div1 = getPieceElement(row1, col1);
  const div2 = getPieceElement(row2, col2);

  if (!div1 || !div2) {
    return null;
  }

  const board = document.getElementById('board');
  const overlay = board ? ensureMixOverlay(board) : null;

  const inner1 = div1.querySelector('.piece');
  const inner2 = div2.querySelector('.piece');
  resetInnerTransforms(inner1, inner2);

  const dx1 = (targetCol - col1) * PIECE_SIZE;
  const dy1 = (targetRow - row1) * PIECE_SIZE;

  div1.style.transition = 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out';
  if (inner2) {
    inner2.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
  }

  div1.style.position = 'relative';
  div1.style.zIndex = String(MIX_TOP_Z);

  return {
    div1,
    div2,
    inner1,
    inner2,
    overlay,
    board,
    mixPieceType,
    dx1,
    dy1,
  };
}

function resetInnerTransforms(inner1, inner2) {
  if (inner1) {
    inner1.style.transform = '';
    inner1.style.transition = '';
  }
  if (inner2) {
    inner2.style.transform = '';
    inner2.style.transition = '';
  }
}

function startPrimaryMergeAnimation({ div1, inner2, dx1, dy1 }) {
  setTimeout(() => {
    div1.style.transform = `translate(${dx1}px, ${dy1}px)`;
    div1.style.opacity = '0';

    if (inner2) {
      inner2.style.transform = 'scale(1.2)';
      inner2.style.filter = 'brightness(1.8) saturate(1.3)';
    }
  }, 0);
}

function scheduleMergeFinale(context, onComplete) {
  setTimeout(() => {
    finalizePrimaryPiece(context.div1);
    if (context.inner2 && context.mixPieceType) {
      runMixHighlightSequence(context, onComplete);
    } else {
      cleanupMixHighlight(null, null, context.inner2, context.div1);
      onComplete();
    }
  }, 300);
}

function finalizePrimaryPiece(div1) {
  if (!div1) return;
  div1.style.visibility = 'hidden';
  div1.style.transition = '';
}

function runMixHighlightSequence(context, onComplete) {
  const { inner2, mixPieceType, overlay, div2, board, div1 } = context;
  inner2.style.transformOrigin = 'center center';

  let flash = null;
  let pop = null;
  if (overlay) {
    flash = createFlashElement();
    pop = createMixPopElement(mixPieceType);
    overlay.appendChild(flash);
    overlay.appendChild(pop);
    positionOverlayElement(flash, div2, board);
    positionOverlayElement(pop, div2, board);
    flash.classList.add('mix-flash--active');
  }

  inner2.style.transition = 'transform 0.25s ease-out, filter 0.25s ease-out';
  inner2.style.transform = 'scale(1.35)';
  inner2.style.filter = 'brightness(2.5) saturate(1.5)';

  if (pop) {
    setTimeout(() => pop.classList.add('mix-pop--visible'), MIX_POP_DELAY_MS);
  }

  setTimeout(() => {
    inner2.style.transform = 'scale(1.0)';
    inner2.style.filter = '';

    const cleanupDelay = pop
      ? Math.max(MIX_FLASH_ANIMATION_MS, MIX_POP_DURATION_MS)
      : MIX_FALLBACK_CLEANUP_DELAY_MS;

    setTimeout(() => {
      cleanupMixHighlight(flash, pop, inner2, div1);
      setTimeout(onComplete, MIX_POST_POP_HOLD_MS);
    }, cleanupDelay);
  }, MIX_RESTORE_DELAY_MS);
}

function cleanupMixHighlight(flash, pop, inner2, div1) {
  if (flash?.parentNode) {
    flash.parentNode.removeChild(flash);
  }
  if (pop?.parentNode) {
    pop.parentNode.removeChild(pop);
  }
  if (inner2) {
    inner2.style.transition = '';
    inner2.style.transformOrigin = '';
  }
  resetMixStyles(div1);
}

/**
 * ラッパー要素のトランジション設定
 * @private
 */
function setupSwapTransitions(div1, div2, row1, col1, row2, col2) {
  // Normalize inner (.piece) transforms to avoid compound transforms
  const inner1 = div1 ? div1.querySelector('.piece') : null;
  const inner2 = div2 ? div2.querySelector('.piece') : null;
  if (inner1) {
    inner1.style.transform = '';
    inner1.style.transition = '';
  }
  if (inner2) {
    inner2.style.transform = '';
    inner2.style.transition = '';
  }

  // Enable transition on wrappers
  if (div1) div1.style.transition = TRANSITION_TRANSFORM;
  if (div2) div2.style.transition = TRANSITION_TRANSFORM;

  // Calculate and apply transforms
  const dx12 = (col2 - col1) * PIECE_SIZE;
  const dy12 = (row2 - row1) * PIECE_SIZE;
  const dx21 = -dx12;
  const dy21 = -dy12;

  setTimeout(() => {
    setTransitionAndTransformUI(div1, TRANSITION_TRANSFORM, `translate(${dx12}px, ${dy12}px)`);
    setTransitionAndTransformUI(div2, TRANSITION_TRANSFORM, `translate(${dx21}px, ${dy21}px)`);
  }, 0);

  return { inner1, inner2 };
}

/**
 * スワップ後のメタデータ同期
 * @private
 */
function syncSwapMetadata(div1, div2, row1, col1, row2, col2) {
  try {
    if (div1) {
      div1.setAttribute('data-row', row2);
      div1.setAttribute('data-col', col2);
      const inner1b = div1.querySelector('.piece');
      if (inner1b) {
        inner1b.setAttribute('data-row', row2);
        inner1b.setAttribute('data-col', col2);
      }
      DRAG_DATA_ATTRS.forEach((attr) => div1.removeAttribute(attr));
    }
    if (div2) {
      div2.setAttribute('data-row', row1);
      div2.setAttribute('data-col', col1);
      const inner2b = div2.querySelector('.piece');
      if (inner2b) {
        inner2b.setAttribute('data-row', row1);
        inner2b.setAttribute('data-col', col1);
      }
      DRAG_DATA_ATTRS.forEach((attr) => div2.removeAttribute(attr));
    }
  } catch (error) {
    // メタデータ更新失敗は非クリティカル - ゲーム続行
    MinimalErrorTracker.info('GAME_INTERACTIONS', 'Metadata update failed');
  }
}

/**
 * スワップアニメーションのUI処理
 * @param {Object} piece1 - ピース1
 * @param {Object} piece2 - ピース2
 * @param {Function} onComplete - 完了コールバック
 */
export function animateSwapUI(piece1, piece2, onComplete) {
  const [row1, col1] = piece1.position;
  const [row2, col2] = piece2.position;

  const div1 = getPieceElement(row1, col1);
  const div2 = getPieceElement(row2, col2);

  if (!div1 || !div2) {
    onComplete();
    return;
  }

  const { inner1, inner2 } = setupSwapTransitions(div1, div2, row1, col1, row2, col2);

  setTimeout(() => {
    swapNodesUI(div1, div2);
    syncSwapMetadata(div1, div2, row1, col1, row2, col2);

    // Clear wrapper transitions/transforms post-swap
    setTransitionAndTransformUI(div1, '', '');
    setTransitionAndTransformUI(div2, '', '');

    // Ensure inners are visible again after swap bookkeeping
    if (inner1) inner1.style.visibility = '';
    if (inner2) inner2.style.visibility = '';
    onComplete();
  }, 300);
}

/**
 * スキルイベントハンドラクリーンアップのUI処理
 */
export function cleanupSkillEventHandlersUI() {
  // documentElementのクラス削除
  document.documentElement.classList.remove('is-skill-targeting');

  // スキルボタンの状態リセット
  const skillButton = document.getElementById('skill-button');
  if (skillButton) {
    skillButton.classList.remove('is-armed');
    skillButton.setAttribute('aria-pressed', 'false');
  }

  // Note: スキルイベントハンドラーのクリーンアップは
  // SkillSystemクラス内で自己管理される（GameState経由での管理は廃止済み）
}

// 後方互換性のためのエクスポート
export { table };
